<template>
    <Card class="mb-4">
        <CardContent class="p-4">
            <!-- Header Row -->
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-4">
                    <img :src="logo" :alt="`${title} logo`" class="w-10 h-10" />
                    <div>
                        <div class="flex items-center gap-2">
                            <span class="text-lg font-medium">{{ title }}</span>
                            <CheckCircle v-if="isConnected" class="h-5 w-5 text-accent-foreground" />
                        </div>
                        <div class="text-sm text-muted-foreground">
                            <div v-if="setInput && !showInput" class="flex items-center gap-1">
                                <Mail class="h-4 w-4" />
                                {{ setInput }}
                            </div>
                            <div v-else class="flex items-center gap-1">
                                <Mail class="h-4 w-4" />
                                Address
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Action Button in header row -->
                <div class="flex items-center gap-2">
                    <LoaderCircle v-if="loading" class="h-4 w-4 animate-spin" />
                    <Button
                        @click="toggleConnection"
                        :disabled="loading"
                        :variant="isConnected ? 'outline' : 'default'"
                        :class="isConnected ? 'border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground' : ''">
                        {{ isConnected ? 'Disconnect' : 'Connect' }}
                    </Button>
                </div>
            </div>
            <!-- Configuration Row: displayed only when editing -->
            <div v-if="showInput" class="mt-4 pt-4 border-t space-y-4">
                <Input
                    v-model="inputValue"
                    type="email"
                    :placeholder="placeholder || 'Enter email address'"
                    :disabled="loading"
                    :class="{ 'border-destructive': inputValue && !isValidEmail(inputValue) }" />
                <div class="flex justify-end gap-2">
                    <Button variant="outline" @click="cancelInput" :disabled="loading"> Cancel </Button>
                    <Button @click="saveInput" :disabled="loading || !isValidEmail(inputValue)">
                        <LoaderCircle v-if="loading" class="h-4 w-4 animate-spin mr-2" />
                        Save
                    </Button>
                </div>
            </div>
        </CardContent>
    </Card>
</template>

<script setup>
import { ref } from 'vue';
import { CheckCircle, Mail, LoaderCircle } from 'lucide-vue-next';
import * as notificationsApi from '../../../api/notifications.api';
import { isValidEmail as isValidEmailUtil } from '../../../utils/validators';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const props = defineProps({
    title: { type: String, required: true },
    logo: { type: String, required: true },
    isConnected: { type: Boolean, default: false },
    placeholder: { type: String, default: null },
    setInput: { type: String, default: null },
    subscriberId: { type: String, default: null },
    providerId: { type: String, default: null }
});

const emit = defineEmits(['update:setInput', 'update:isConnected']);

const showInput = ref(false);
const loading = ref(false);
const inputValue = ref(props.setInput || '');

const isValidEmail = (input) => {
    return isValidEmailUtil(input);
};

const toggleConnection = async () => {
    loading.value = true;
    try {
        const connected = !props.isConnected;
        if (connected) {
            showInput.value = true;
        } else {
            await notificationsApi.deleteSubscriberCredentials(props.subscriberId, props.providerId);
            await notificationsApi.updateSubscriber(props.subscriberId, '');
            emit('update:setInput', null);
            inputValue.value = '';
            showInput.value = false;
            emit('update:isConnected', false);
        }
    } catch (error) {
        console.error('Error toggling email connection:', error);
    } finally {
        loading.value = false;
    }
};

const saveInput = async () => {
    loading.value = true;
    try {
        await notificationsApi.updateSubscriber(props.subscriberId, inputValue.value);
        emit('update:setInput', inputValue.value);
        emit('update:isConnected', true);
        showInput.value = false;
    } catch (error) {
        console.error('Failed to update subscriber:', error);
    } finally {
        loading.value = false;
    }
};

const cancelInput = () => {
    inputValue.value = props.setInput || '';
    emit('update:isConnected', false);
    showInput.value = false;
};
</script>
